/* eslint-disable max-len */
import { h, Fragment } from 'preact';
import { formatMoney } from 'utils/storefront/format-resource';
import { useEffect, useMemo, useState } from 'preact/hooks';
import VariantSelector from './variant-selector';
import Image from '../utils/image';
import Icon from '../utils/icon';
import ProductHighlight from './product-highlight';
import ShopPayProduct from './shop-pay-product';

const handleNotifyMe = (e, product, currentVariant) => {
  e.preventDefault();
  const openPanel = new CustomEvent('openPanel', {
    detail: {
      currentVariant,
      product,
    },
  });

  const backInStockFormContainer = document.querySelector(
    '[data-target="back-in-stock-form-container"]'
  );
  
  if (backInStockFormContainer) {
    backInStockFormContainer.dispatchEvent(openPanel);
  }
};


const Price = ({ currentVariant, product, atcSticky, isB2B, quantity }) => {
  if (!currentVariant) {
    return (
      <div>
        {window.TMW.Strings.product.price.noPrice}
      </div>
    );
  }

  const [effectiveQuantity, setEffectiveQuantity] = useState(quantity);

  useEffect(() => {
    setEffectiveQuantity(quantity);
  }, [quantity]);

  const compareAgainstRetailPrice = isB2B && currentVariant?.retailPrice?.amount > 0;

  const calculatePrice = (basePrice) => {

    if (!product.sellInSets) {
      return {
        amount: basePrice?.amount || 0,
        currencyCode: basePrice?.currencyCode || currentVariant?.price.currencyCode,
      };
    }
  
    const multiplier = Math.max(effectiveQuantity, parseInt(product.sellInSets, 10) || 1);
  
    return {
      amount: multiplier * (basePrice?.amount || 0),
      currencyCode: basePrice?.currencyCode || currentVariant?.price.currencyCode,
    };
  };

  const price = () => {
    const className = compareAgainstRetailPrice
      ? 'text-dark-green ml-2'
      : currentVariant.compare_at_price?.amount
      ? 'text-red ml-2'
      : '';

    const priceObject = calculatePrice(currentVariant?.price);

    return (
      <span className={`price ${className}`}>
        {compareAgainstRetailPrice && !atcSticky
          ? `${product.quantityPriceBreaksConfigured ? 'FROM ' : ''}${formatMoney(priceObject || currentVariant?.price)} ${window.TMW.Strings.product.b2b}`
          : formatMoney(priceObject || currentVariant?.price)}
      </span>
    );
  };

  const compareAtPrice = () => {
    const baseAmount = compareAgainstRetailPrice
      ? currentVariant?.retailPrice
      : currentVariant.compare_at_price;

    const compareAtPriceObject = baseAmount ? calculatePrice(baseAmount) : null;
   
    return compareAtPriceObject?.amount ? (
      <span className="compareAtPrice">
        <del>{formatMoney(compareAtPriceObject)}</del>
      </span>
    ) : null;
  };

  return (
    <div className='hidden md:block'>
      {compareAtPrice()}
      {price()}
    </div>
  );
};

const ProductName = ({ currentVariant, atcSticky }) => (
  <h1 className={`${atcSticky ? '' : 'mt-14'} uppercase h3 hidden md:block`}>{currentVariant?.title}</h1>
);

const ProductQuantity = ({
  quantity, 
  setQuantity, 
  setErrorMessage, 
  atcSticky, 
	product,
  variant,
	isB2B
}) => {
  const max =
		(variant?.quantityRule?.max|| variant?.inventoryQuantity) ??
		1000;
  const min = variant.quantityRule?.min || 1;
  const maxQuantityMessage = variant.quantityRule?.max ? `Maximum of ${variant.quantityRule?.max}` : window.TMW.Strings.product.inventoryNotice.replace('{{ number }}' , max);
  const minQuantityMessage = variant.quantityRule?.min ? `Minimum of ${variant.quantityRule?.min}` : window.TMW.Strings.product.inventoryNotice.replace('{{ number }}' , min);
  const increment = variant?.quantityRule?.increment ?? 1;
  const inventoryPolicy = variant.inventory_policy;
  let continueSellingWhenOutOfStock = false

  if (inventoryPolicy === 'continue') {
    if (isB2B) {
      continueSellingWhenOutOfStock = true  
    } else if (!variant.continueSellingOnlyForB2b) {
      continueSellingWhenOutOfStock = true  
    }
  }

  const handleInput = (e) => {
    let newQuantity = Number.parseInt(e.target.value, 10);
    if (Number.isNaN(newQuantity)) {
      setQuantity(min);
      setErrorMessage(null);
    } else if (newQuantity > max) {
			setQuantity(max);
      setErrorMessage(maxQuantityMessage);
    } else if (newQuantity < min) {
			newQuantity = min
      setErrorMessage(minQuantityMessage);
    } else if (increment && (newQuantity % increment)) {
			newQuantity = Math.max(Math.min(Math.floor(newQuantity / increment) * increment, min), min)
		} else {
      setErrorMessage(null);
    }
	}

  const handleInputBlur = (e) => {
    const newQuantity = Number.parseInt(e.target.value, 10);
    if (newQuantity > max) {
      setErrorMessage(maxQuantityMessage);
      setQuantity(max);
    }

    const quantityUpdated = new CustomEvent('currentVariantQuantityUpdated', {
      detail: {
        quantity: newQuantity
      }
    });
    document.dispatchEvent(quantityUpdated);
  }

  const handleMinusButtonClick = (e) => {
    e.preventDefault();
		const newQuantity = Math.max(quantity - increment, increment);
		if (newQuantity <= max) setErrorMessage(null);
    setQuantity(newQuantity);
    const quantityUpdated = new CustomEvent('currentVariantQuantityUpdated', {
      detail: {
        quantity: newQuantity
      }
    });
    document.dispatchEvent(quantityUpdated);
  };

  const handlePlusButtonClick = (e) => {
    e.preventDefault();
    const newQuantity = continueSellingWhenOutOfStock ? quantity + increment : Math.min(quantity + increment, max);
    const attemptedQuantity = quantity + increment;
		
    if (!continueSellingWhenOutOfStock && attemptedQuantity > max) {
      setErrorMessage(maxQuantityMessage);
    } else {
      setErrorMessage(null);
    }

		setQuantity(newQuantity);

    const quantityUpdated = new CustomEvent('currentVariantQuantityUpdated', {
      detail: {
        quantity: attemptedQuantity
      }
    });
    document.dispatchEvent(quantityUpdated);
  };


  const plusBtnDisabled = !continueSellingWhenOutOfStock && !variant.availableForSale 
  const decrementDisabled = quantity <= variant.quantityRule?.min || quantity === 1 || (!variant.availableForSale && !continueSellingWhenOutOfStock)
  const inputDisabled = product.quantityPriceBreaksConfigured || variant.quantityRule?.min > 1 || variant.quantityRule?.max || variant.quantityRule?.increment > 1 || (!variant.availableForSale && !continueSellingWhenOutOfStock)

  return (
    <div className={`${atcSticky ? 'hidden' : 'flex relative items-center gap-2 px-2 py-3 border border-secondary utility-2 text-secondary'}`}>
      <button
        className={`w-5 ${decrementDisabled && 'opacity-50'}`}
        onClick={handleMinusButtonClick}
				disabled={decrementDisabled}
        aria-label={window.TMW.Strings.cart.remove}
      >
        <Icon name="minus"/>
      </button>
      <input
        type="number"
        value={quantity}
        className="w-5 p-0 text-center border-0 bg-white"
        id="Quantity"
        onInput={handleInput}
        onBlur={handleInputBlur}
				disabled={inputDisabled}
				step={increment}
        min={increment}
        max={max}
      />
      <button
				disabled={plusBtnDisabled}
        className={`w-5 ${plusBtnDisabled && 'opacity-50'}`}
        onClick={handlePlusButtonClick}
        aria-label={window.TMW.Strings.cart.add}
      >
        <Icon name="plus"/>
      </button>
    </div>
  );
};

const AddToCartButton = ({ currentVariant, quantity, product, atcSticky, isB2B }) => {

  const handleAddToCart = (e) => {
    e.preventDefault();
    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: currentVariant?.id,
          quantity,
          attributes: [
            { key: '_quantityPriceBreaksConfigured', value: product.quantityPriceBreaksConfigured },
            { key: '_inventoryQuantity', value: currentVariant.inventoryQuantity },
            {
              key: '_quantityPriceBreaks',
              value: currentVariant.quantityPriceBreaks.map(({ minimumQuantity, price }) => ({
                minimumQuantity,
                price,
              })),
            },
            {
              key: '_retailPrice', 
              value: { 
                amount: currentVariant.retailPrice?.amount, 
                currencyCode: currentVariant.retailPrice?.currencyCode
              }
            },
            {
              key: '_quantityRule', 
              value: { 
                increment: currentVariant.quantityRule?.increment,
                min: currentVariant.quantityRule?.min,
                max: currentVariant.quantityRule?.max,
              }
            },
          ],
        },
      },
    });
    document.dispatchEvent(addToCartEvent);  
  };

  let variantAvailableForSale = 
  currentVariant?.availableForSale;

  if (isB2B !== true && quantity > currentVariant?.inventoryQuantity) {
    if (currentVariant.continueSellingOnlyForB2b === true) {
      variantAvailableForSale = false;
    }
  } else if (quantity === 0) {
    variantAvailableForSale = false;
  }

  const isNotifyMe = currentVariant?.notifyMe === null || currentVariant?.notifyMe === true;
  const btn = !variantAvailableForSale &&
    isNotifyMe  ?
    <button
      type="submit"
      className={'w-full button button--secondary-on-white' }
      onClick={(e) => handleNotifyMe(e, product, currentVariant)}
    >
      
      {window.TMW.Strings.product.notifyMe}
    </button> : 
    <button
      type="submit"
      className="w-full items-center button button--primary-on-white atc-sticky:w-64"
      disabled={!variantAvailableForSale}
      onClick={(e) => handleAddToCart(e)}
    >
      <div className='atc-sticky:flex atc-sticky:justify-between atc-sticky:w-full lg:atc-sticky:justify-center items-center'>
        <span className="lg:mb-0 items-center">
          {currentVariant
            ? variantAvailableForSale
              ? window.TMW.Strings.product.addToCart
              : window.TMW.Strings.product.outOfStock
            : window.TMW.Strings.product.unavailable}
        </span>
        <span className='hidden atc-sticky:block lg:atc-sticky:hidden'>
          <Price currentVariant={currentVariant} product={product} atcSticky={atcSticky} isB2B={isB2B} quantity={quantity} />
        </span>
      </div>
    </button>

    return btn;
};

const VolumePricing = ({ product, variant, showMore, onClick }) => {
  if (!product.quantityPriceBreaksConfigured || !variant.quantityPriceBreaks?.length) return null;

  return (
    <div className="py-6">
      <span className="block utility-2 pb-2 ">Volume Pricing</span>
			<ul className="prices">
        <li className="py-4 utility-2-normal border-y flex justify-between">
          <span>{variant.quantityRule?.min}+</span>
          <span>
            {formatMoney(variant.price)}/ea
          </span>
        </li>
				{
					variant.quantityPriceBreaks?.map(({ minimumQuantity, price }, i) => (
						<li
							key={minimumQuantity}
							className={`py-4 utility-2-normal border-b flex justify-between ${
								i >= 2 ? 'show-more-item hidden' : ''
							}`}
						>
							<span>
								{minimumQuantity}
								<span aria-hidden="true">+</span>
							</span>
							<span>{formatMoney({ amount: price })}/ea</span>
						</li>
					))
				}
				{
					showMore && (
						<button
							onClick={(e) => {
								e.preventDefault()
								for (const el of document.querySelectorAll('li.show-more-item.hidden')) {
									el.classList.toggle('hidden')
								}
								onClick(false)
							}}
							className="volume-pricing-show-more utility-2 border-b pt-2 border-black"
						>
							show more
						</button>
					)
				}
      </ul>
    </div>
  );
};

const ProductForm = ({
  product, 
  selectedOptions,
  setSelectedOptions,
  availabilityMatrix, 
  currentVariant,
  atcSticky,
  showTrigger,
	isB2B,
  quantity,
  setQuantity
}) => {

	const [quantityErrorMessage, setQuantityErrorMessage] = useState(null);

	useEffect(() => {
    setQuantity(currentVariant?.quantityRule?.min || 1)
		setQuantityErrorMessage(null);
	}, [currentVariant])

  const variantSelectorButtons = useMemo(
    () =>
      !product.hasOnlyDefaultVariant &&
      product.options.map((option) => (
        <VariantSelector
          key={option.name}
          option={option}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          variants={product.variants}
          availabilityMatrix={availabilityMatrix}
        />
      )),
    [product, availabilityMatrix],
  );

  const price = useMemo(
    () => <Price currentVariant={currentVariant} product={product} isB2B={isB2B} quantity={quantity} />,
    [currentVariant,product,quantity],
  );

  const productName = useMemo(
    () => <ProductName currentVariant={product} atcSticky={atcSticky} />,
    [product],
  );

	const [showMore, setShowMore] = useState(currentVariant.quantityPriceBreaks.length > 2)
	useEffect(() => {
		setShowMore(currentVariant.quantityPriceBreaks.length > 2)
	}, [currentVariant])

	const ProductActions = () => {
		let quantityRuleString = []
		if (currentVariant.quantityRule?.increment > 1) quantityRuleString.push(`Increments of ${currentVariant.quantityRule?.increment}`)
		if (currentVariant.quantityRule?.min > 1) quantityRuleString.push(`Minimum of ${currentVariant.quantityRule?.min}`)
		if (currentVariant.quantityRule?.max != null) quantityRuleString.push(`Maximum of ${currentVariant.quantityRule?.max}`)
		quantityRuleString = quantityRuleString.join(' | ')
		
		const activePriceBreak = [...currentVariant.quantityPriceBreaks]
			.sort((a, b) => b.minimumQuantity - a.minimumQuantity)
			.find((priceBreak) => quantity >= priceBreak.minimumQuantity);

		return (
			<>
      {isB2B && <div className="mt-6 uppercase atc-sticky:hidden utility-2-normal py-2">{window.TMW.Strings.product.quantity}<span className="font-bold ml-2">{quantity}</span></div>}
				<div
					className={`${
						atcSticky
							? ''
							: 'atc-sticky:gap-0 atc-sticky:w-full atc-sticky:m-0 lg:atc-sticky:w-56'
					} flex gap-2 items-center`}
				>
					{!atcSticky && <ProductQuantity
						quantity={quantity}
						setQuantity={setQuantity}
						atcSticky={atcSticky}
						product={product}
						variant={currentVariant}
						setErrorMessage={setQuantityErrorMessage}
						isB2B={isB2B}
					/>}
					{
						product.quantityPriceBreaksConfigured && !atcSticky ? (
							<span className="px-3 utility-2-normal">
								AT {formatMoney({amount: activePriceBreak?.price || currentVariant?.price.amount, currency: currentVariant?.price.currencyCode})}/EA
							</span>
						) : (
							<AddToCartButton
								currentVariant={currentVariant}
								quantity={quantity}
								product={product}
								atcSticky={atcSticky}
								isB2B={isB2B}
							/>
						)
					}
				</div>
				{quantityErrorMessage && <p className="block text-green py-2">{quantityErrorMessage}</p>}
				{!atcSticky &&
          (product.quantityPriceBreaksConfigured ||
            currentVariant?.quantityRule?.increment !== 1 ||
            currentVariant?.quantityRule?.min !== 1 ||
            (currentVariant?.quantityRule?.max > 0 && !atcSticky)) && (
            <p className="py-2">
              <span>{quantityRuleString}</span>
            </p>
          )
        }
				<ShopPayProduct currentVariant={currentVariant} />
				{!atcSticky && <VolumePricing product={product} variant={currentVariant} showMore={showMore} onClick={setShowMore} />}
				{
					product.quantityPriceBreaksConfigured && !atcSticky && (
						<AddToCartButton
							currentVariant={currentVariant}
							quantity={quantity}
							product={product}
							atcSticky={atcSticky}
						/>
					)
				}
			</>
		)};
	

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
		const oldVariant = searchParams.get('variant');
    searchParams.set('variant', currentVariant?.id.split('/ProductVariant/')[1]);
		const newVariant = searchParams.get('variant');
    const newRelativePathQuery = `${
      window.location.pathname
    }?${searchParams.toString()}`;
		if (oldVariant !== newVariant) window.history.replaceState(null, "", newRelativePathQuery)
  }, [currentVariant]);

  useEffect(() => {
    const productUpdatedEvent = new CustomEvent('product:updated', {
      detail: {
        payload: {
          product,
          variant: currentVariant,
        },
      },
    });

    document.dispatchEvent(productUpdatedEvent);
  }, [product, currentVariant]);

  return (
    <form
      method="post"
      action="/cart/add"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate="novalidate"
      data-product-form
      className={`${atcSticky ? 'atc-sticky:p-0 atc-sticky:bg-white' : ''} px-3 md:px-16`}
    >
      <div className={`${atcSticky ? 'atc-sticky:flex' : ''}`}>
        <Image
          src={currentVariant?.image || product?.images[0]?.src}
          classes={`${atcSticky ? 'lg:atc-sticky:block' : ''} hidden w-44 h-28 object-cover`}
        />
        <div className={`${atcSticky ? 'atc-sticky:flex atc-sticky:justify-between atc-sticky:items-center atc-sticky:w-full atc-sticky:p-3 lg:atc-sticky:py-6 lg:atc-sticky:px-10' : ''}`}>
          <div className={`${atcSticky ? 'atc-sticky:hidden lg:atc-sticky:flex atc-sticky:flex-col atc-sticky:gap-y-1' : ''}`}>
            {productName}
            <div className={`${atcSticky ? 'atc-sticky:flex atc-sticky:flex-col-reverse atc-sticky:gap-y-1' : ''}`}>
              {price}
              <div className={`${atcSticky ? 'atc-sticky:flex atc-sticky:gap-4' : ''}`}>
                {variantSelectorButtons}
                <div className={`${atcSticky ? 'atc-sticky:hidden' : 'atc-sticky:invisible'}`}>
                  <ProductActions />
                </div>
                <ProductHighlight 
									currentVariant={currentVariant} 
									productShippingStatus={product.shippingStatus}
									product={product}
									highlights={product.highlights}
									description={product.description}
									atcSticky={atcSticky} 
									quantity={quantity}
									isB2B={isB2B} />
              </div>
              {showTrigger && <div className='h-0' data-target='sticky-atc-trigger'/>}
            </div>
          </div>
          <div className={`${atcSticky ? 'atc-sticky:block atc-sticky:w-full lg:atc-sticky:w-auto' : 'hidden'}`}>
						<ProductActions />
          </div>
        </div>
      </div>
    </form>
  );  
};

export default ProductForm